import React from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import useWakeLock from "@/hooks/useWakeLock";
import { useRealtimeEvents, useConnectionStatus } from "@/hooks/useRealtimeEvents";
import { setupPushNotifications, resetPushSetup } from "@/lib/push";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Building2, Users, Flame, ChefHat, GlassWater,
  CookingPot, BadgeDollarSign, Bike, Boxes, Receipt, BarChart3,
  Settings, LogOut, Menu, Moon, Sun, Pizza, Sparkles, Sandwich, ShieldCheck, FileText, Salad
} from "lucide-react";
import { cn } from "@/lib/utils";

const NAV_BY_ROLE = {
  SUPER_ADMIN: [
    { to: "/admin", icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard" },
    { to: "/admin/clientes", icon: Building2, label: "Clientes", testid: "nav-clientes" },
    { to: "/admin/sucursales", icon: ShieldCheck, label: "Sucursales", testid: "nav-sucursales" },
    { to: "/admin/pagos", icon: Receipt, label: "Pagos", testid: "nav-pagos" },
    { to: "/admin/auditoria", icon: FileText, label: "Auditoría", testid: "nav-auditoria" },
  ],
  ADMIN_SUCURSAL: [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard" },
    { to: "/mesero", icon: Sandwich, label: "Salón / Tomar Orden", testid: "nav-mesero" },
    { to: "/mesero/nueva", icon: Sparkles, label: "Nueva Orden", testid: "nav-nueva-orden" },
    { to: "/menu", icon: Pizza, label: "Menú", testid: "nav-menu" },
    { to: "/acompanamientos", icon: Salad, label: "Acompañamientos", testid: "nav-acompanamientos" },
    { to: "/areas", icon: Flame, label: "Áreas", testid: "nav-areas" },
    { to: "/opciones-rapidas", icon: Sparkles, label: "Opciones rápidas", testid: "nav-opciones" },
    { to: "/mesas", icon: Sandwich, label: "Mesas", testid: "nav-mesas" },
    { to: "/ordenes", icon: Receipt, label: "Órdenes", testid: "nav-ordenes" },
    { to: "/cocina", icon: ChefHat, label: "Cocina", testid: "nav-cocina" },
    { to: "/bebidas", icon: GlassWater, label: "Bebidas", testid: "nav-bebidas" },
    { to: "/freidora", icon: CookingPot, label: "Freidora", testid: "nav-freidora" },
    { to: "/caja", icon: BadgeDollarSign, label: "Caja", testid: "nav-caja" },
    { to: "/reparto", icon: Bike, label: "Reparto", testid: "nav-reparto" },
    { to: "/inventario", icon: Boxes, label: "Inventario", testid: "nav-inventario" },
    { to: "/gastos", icon: Receipt, label: "Gastos", testid: "nav-gastos" },
    { to: "/reportes", icon: BarChart3, label: "Reportes", testid: "nav-reportes" },
    { to: "/usuarios", icon: Users, label: "Usuarios", testid: "nav-usuarios" },
    { to: "/configuracion", icon: Settings, label: "Configuración", testid: "nav-config" },
  ],
  MESERO: [
    { to: "/mesero", icon: LayoutDashboard, label: "Mis pedidos", testid: "nav-mis-areas" },
    { to: "/ordenes", icon: Receipt, label: "Órdenes", testid: "nav-ordenes" },
  ],
  COCINA: [
    { to: "/mis-areas", icon: LayoutDashboard, label: "Mis pedidos", testid: "nav-mis-areas" },
    { to: "/ordenes", icon: Receipt, label: "Órdenes", testid: "nav-ordenes" },
  ],
  BEBIDAS: [
    { to: "/mis-areas", icon: LayoutDashboard, label: "Mis pedidos", testid: "nav-mis-areas" },
    { to: "/ordenes", icon: Receipt, label: "Órdenes", testid: "nav-ordenes" },
  ],
  FREIDORA: [
    { to: "/mis-areas", icon: LayoutDashboard, label: "Mis pedidos", testid: "nav-mis-areas" },
    { to: "/ordenes", icon: Receipt, label: "Órdenes", testid: "nav-ordenes" },
  ],
  CAJA: [
    { to: "/caja", icon: BadgeDollarSign, label: "Caja", testid: "nav-caja" },
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard" },
    { to: "/reparto", icon: Bike, label: "Reparto", testid: "nav-reparto" },
    { to: "/reportes", icon: BarChart3, label: "Reportes", testid: "nav-reportes" },
  ],
  REPARTIDOR: [{ to: "/reparto", icon: Bike, label: "Mis Entregas", testid: "nav-reparto" }],
};

export default function Layout() {
  const { user, logout, sucursales, selectedSucursalId, setSelectedSucursalId } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const { online, sse } = useConnectionStatus();

  // Pantalla siempre activa mientras el usuario esté autenticado y la app esté visible.
  useWakeLock(!!user);

  // Conexión SSE global del Layout: garantiza que el banner refleje la realidad
  // incluso en pantallas que no se suscriben a eventos en tiempo real (Reportes,
  // configuración, etc.). Los componentes hijos que necesitan eventos siguen
  // levantando su propia suscripción sin problema.
  useRealtimeEvents(() => { /* noop: solo mantenemos la conexión viva */ }, () => { /* noop */ });

  // Push Notifications (Android APK únicamente). Fire-and-forget; cualquier
  // fallo en permisos, token o backend NO bloquea login ni operación.
  React.useEffect(() => {
    if (!user) return;
    setupPushNotifications(user).catch(() => { /* no-op: ya se loguea dentro */ });
  }, [user]);

  if (!user) return null;
  const nav = NAV_BY_ROLE[user.role] || [];

  const handleLogout = async () => {
    resetPushSetup();
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed lg:static inset-y-0 left-0 z-40 w-72 border-r border-border bg-card transition-transform duration-200",
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
        data-testid="app-sidebar"
      >
        <div className="flex h-20 items-center gap-3 px-6 border-b border-border">
          <img src="/mkitchen-logo.png" alt="M-Kitchen" className="h-11 w-11 rounded-xl object-contain bg-black p-1" />
          <div>
            <div className="text-lg font-black tracking-tight">M-Kitchen</div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Admin POS</div>
          </div>
        </div>
        <nav className="flex flex-col gap-1 px-3 py-4 overflow-y-auto h-[calc(100vh-5rem)]">
          {nav.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/admin" || item.to === "/dashboard" || item.to === "/mesero"}
                onClick={() => setSidebarOpen(false)}
                data-testid={item.testid}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-semibold transition-colors",
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground/80 hover:bg-secondary hover:text-foreground"
                  )
                }
              >
                <Icon className="h-5 w-5" strokeWidth={2} />
                {item.label}
              </NavLink>
            );
          })}
          <div className="mt-auto pt-4 border-t border-border space-y-2">
            <div className="px-3 py-2">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Sesión</div>
              <div className="text-sm font-semibold truncate" data-testid="current-user-email">{user.email}</div>
              <div className="text-xs text-muted-foreground">{user.role}</div>
            </div>
            <Button
              variant="outline"
              className="w-full justify-start gap-3"
              onClick={toggle}
              data-testid="theme-toggle-btn"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              {theme === "dark" ? "Modo claro" : "Modo oscuro"}
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 text-destructive hover:text-destructive"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" />
              Cerrar sesión
            </Button>
          </div>
        </nav>
      </aside>

      {/* Backdrop mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="h-16 border-b border-border bg-card/60 backdrop-blur px-4 lg:px-8 flex items-center justify-between sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen((s) => !s)}
              data-testid="open-sidebar-btn"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-base sm:text-lg font-bold tracking-tight">Panel operativo</h1>
              <p className="text-xs text-muted-foreground">{new Date().toLocaleDateString("es-MX", { timeZone: "America/Mexico_City", weekday: "long", day: "numeric", month: "long" })}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Selector de sucursal — solo si tiene más de 1 */}
            {sucursales && sucursales.length > 1 && (
              <select
                value={selectedSucursalId || ""}
                onChange={(e) => setSelectedSucursalId(e.target.value)}
                className="h-9 rounded-md border border-border bg-card px-3 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="sucursal-selector"
                aria-label="Seleccionar sucursal"
              >
                {sucursales.map((s) => (
                  <option key={s.id} value={s.id}>{s.nombre}</option>
                ))}
              </select>
            )}
            <Button variant="outline" size="icon" onClick={toggle} className="hidden sm:flex" data-testid="theme-toggle-topbar">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <div className="hidden sm:block px-3 py-1.5 rounded-md bg-secondary text-xs font-bold uppercase tracking-wider">
              {user.role}
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8 overflow-x-hidden" data-testid="main-content">
          {(!online || !sse) && (
            <div className="mb-4 rounded-md border border-amber-500/40 bg-amber-50 dark:bg-amber-950/30 px-4 py-2 text-sm font-semibold text-amber-700 dark:text-amber-200 flex items-center gap-2" data-testid="connection-banner">
              <span className="inline-block h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
              {!online
                ? "Sin conexión. Intentando reconectar…"
                : "Conexión inestable. Esperando respuesta del servidor."}
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
